import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import FuturesTicker from "../components/FuturesTicker";
import "flag-icons/css/flag-icons.min.css";
import { FaCalendarAlt, FaFire, FaClock, FaChartLine, FaExternalLinkAlt } from "react-icons/fa";

// --- HOLIDAY DATA ---
const HOLIDAYS_2025 = [
  { name: "New Year's Day", date: "2025-01-01" },
  { name: "Martin Luther King Jr. Day", date: "2025-01-20" },
  { name: "Presidents' Day", date: "2025-02-17" },
  { name: "Good Friday", date: "2025-04-18" },
  { name: "Memorial Day", date: "2025-05-26" },
  { name: "Juneteenth", date: "2025-06-19" },
  { name: "Independence Day", date: "2025-07-04" },
  { name: "Labor Day", date: "2025-09-01" },
  { name: "Thanksgiving Day", date: "2025-11-27" },
  { name: "Christmas Day", date: "2025-12-25" },
];

const HOLIDAYS_2026 = [
  { name: "New Year's Day", date: "2026-01-01" },
  { name: "Martin Luther King Jr. Day", date: "2026-01-19" },
  { name: "Presidents' Day", date: "2026-02-16" },
  { name: "Good Friday", date: "2026-04-03" },
  { name: "Memorial Day", date: "2026-05-25" },
  { name: "Juneteenth", date: "2026-06-19" },
  { name: "Independence Day (Observed)", date: "2026-07-03" },
  { name: "Labor Day", date: "2026-09-07" },
  { name: "Thanksgiving Day", date: "2026-11-26" },
  { name: "Christmas Day", date: "2026-12-25" },
];

// --- URLS ---
const EARNINGS_JSON_URL = "https://trapound-earnings.s3.us-east-1.amazonaws.com/earnings-events.json";

// Normalize legacy ES/NQ/YM codes to S&P, Nasdaq, Dow
const MARKET_LABEL_MAP = {
  ES: "S&P",
  "S&P": "S&P",
  "S&P 500": "S&P",
  NQ: "Nasdaq",
  NASDAQ: "Nasdaq",
  Nasdaq: "Nasdaq",
  YM: "Dow",
  DOW: "Dow",
  Dow: "Dow",
};

// Official 2026 Dow 30 Set (Alphabet GOOG/GOOGL replaced Verizon VZ in June 2026)
const DOW_30_SET = new Set([
  "AAPL", "AMGN", "AMZN", "AXP", "BA", "CAT", "CRM", "CSCO", "CVX", "DIS",
  "GOOG", "GOOGL", "GS", "HD", "HON", "IBM", "JNJ", "JPM", "KO", "MCD",
  "MMM", "MRK", "MSFT", "NKE", "NVDA", "PG", "SHW", "TRV", "UNH", "V", "WMT"
]);

// Official Nasdaq 100 Set
const NASDAQ_100_SET = new Set([
  "AAPL", "ABNB", "ADBE", "ADI", "ADP", "ADSK", "AEP", "AMAT", "AMD", "AMGN",
  "AMZN", "ANSS", "APP", "ARM", "ASML", "AVGO", "AZN", "BIIB", "BKNG", "BKR",
  "CCEP", "CDNS", "CDW", "CEG", "CHTR", "CMCSA", "COST", "CPRT", "CRWD", "CSCO",
  "CSGP", "CSX", "CTAS", "CTSH", "DASH", "DDOG", "DXCM", "EA", "EXC", "FANG",
  "FAST", "FTNT", "GEHC", "GFS", "GILD", "GOOG", "GOOGL", "HON", "IDXX", "INTC",
  "INTU", "ISRG", "KDP", "KHC", "KLAC", "LIN", "LRCX", "LULU", "MAR", "MCHP",
  "MDB", "MDLZ", "MELI", "META", "MNST", "MRVL", "MSFT", "MSTR", "MU", "NFLX",
  "NVDA", "NXPI", "ODFL", "ON", "ORLY", "PANW", "PAYX", "PCAR", "PDD", "PEP",
  "PLTR", "PYPL", "QCOM", "REGN", "ROP", "ROST", "SBUX", "SNPS", "TEAM", "TMUS",
  "TSLA", "TTD", "TTWO", "TXN", "VRSK", "VRTX", "WBD", "WDAY", "XEL", "ZS"
]);

// Curated True Index Movers (~35 heavyweights that genuinely move ES, NQ, or YM futures)
const TRUE_INDEX_MOVERS_SET = new Set([
  // Mag 7 & Mega-Cap Tech / AI Bellwethers (Move both ES and NQ futures)
  "AAPL", "MSFT", "NVDA", "AMZN", "META", "GOOGL", "GOOG", "TSLA", "AVGO",
  "AMD", "NFLX", "ORCL", "CRM", "ADBE", "MU", "PLTR", "ASML", "QCOM", "INTC",
  // Top S&P 500 & Dow 30 Heavyweights (Move ES and/or YM futures)
  "BRK-B", "LLY", "JPM", "V", "UNH", "WMT", "MA", "COST", "XOM",
  "HD", "PG", "JNJ", "BAC", "ABBV", "CVX",
  // High-Price Dow 30 Movers (Move YM futures due to Dow price-weighting)
  "GS", "CAT", "AMGN", "MCD", "AXP", "TRV", "SHW", "IBM", "BA"
]);

function normalizeMarkets(markets, symbol) {
  const mapped = Array.isArray(markets)
    ? markets.map((m) => MARKET_LABEL_MAP[m] || m)
    : [];
  const marketSet = new Set(mapped);

  const cleanSym = String(symbol || "").trim().toUpperCase();
  if (NASDAQ_100_SET.has(cleanSym)) marketSet.add("Nasdaq");
  if (DOW_30_SET.has(cleanSym)) {
    marketSet.add("Dow");
  } else if (cleanSym === "VZ") {
    // Verizon was replaced by Alphabet in the Dow in June 2026
    marketSet.delete("Dow");
  }

  // Keep consistent display order: S&P -> Nasdaq -> Dow
  const order = ["S&P", "Nasdaq", "Dow"];
  return Array.from(marketSet).sort((a, b) => {
    const idxA = order.indexOf(a);
    const idxB = order.indexOf(b);
    return (idxA === -1 ? 99 : idxA) - (idxB === -1 ? 99 : idxB);
  });
}

function isTrueMajorMover(symbol) {
  const cleanSym = String(symbol || "").trim().toUpperCase();
  return TRUE_INDEX_MOVERS_SET.has(cleanSym);
}

export default function EconomicEvents() {
  // -----------------------------
  // Earnings Data & Filters
  // -----------------------------
  const [earnings, setEarnings] = useState([]);
  const [earningsPublishTime, setEarningsPublishTime] = useState("");
  const [earningsLoading, setEarningsLoading] = useState(true);
  const [earningsSearch, setEarningsSearch] = useState("");
  const [majorMoversOnly, setMajorMoversOnly] = useState(true); // Default to Major Movers
  const [selectedMarket, setSelectedMarket] = useState("ALL"); // ALL, S&P, Nasdaq, Dow
  const [showPastDays, setShowPastDays] = useState(false); // Starts from current day by default

  const [activeTab, setActiveTab] = useState("events");

  const isDark =
    typeof window !== "undefined" &&
    document.documentElement.classList.contains("dark");

  // Get today's local YYYY-MM-DD string
  const todayDateStr = useMemo(() => {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }, []);

  // --- DATA FETCHING (Earnings) ---
  useEffect(() => {
    const cacheBuster = new Date().getTime();
    fetch(`${EARNINGS_JSON_URL}?t=${cacheBuster}`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (data?.updated_at) {
          const dt = new Date(data.updated_at);
          if (!isNaN(dt.getTime())) {
            setEarningsPublishTime(
              dt.toLocaleString("en-US", {
                month: "short",
                day: "numeric",
                hour: "numeric",
                minute: "2-digit",
                timeZoneName: "short",
              })
            );
          }
        } else if (data?.publish_time_est) {
          setEarningsPublishTime(data.publish_time_est);
        }

        const list =
          (data && Array.isArray(data.events) && data.events) ||
          (data && Array.isArray(data.earnings) && data.earnings) ||
          [];

        setEarnings(list);
        setEarningsLoading(false);
      })
      .catch((e) => {
        console.error("Failed to load earnings:", e);
        setEarnings([]);
        setEarningsLoading(false);
      });
  }, []);

  // Convert exact_time ("7:00 AM ET") to minutes since midnight for chronological sorting
  function parseTimeMinutes(timeStr, fallbackHourCode) {
    if (timeStr) {
      const m = String(timeStr).match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
      if (m) {
        let hh = parseInt(m[1], 10);
        const mm = parseInt(m[2], 10);
        const ampm = (m[3] || "").toUpperCase();
        if (ampm === "PM" && hh < 12) hh += 12;
        if (ampm === "AM" && hh === 12) hh = 0;
        return hh * 60 + mm;
      }
    }
    const code = (fallbackHourCode || "").toLowerCase();
    if (code === "bmo") return 8 * 60; // Default 8:00 AM
    if (code === "dmh") return 12 * 60; // Default 12:00 PM
    if (code === "amc") return 16 * 60 + 15; // Default 4:15 PM
    return 23 * 60 + 59;
  }

  // --- FILTER & GROUP EARNINGS BY DAY (Starting from Today) ---
  const groupedEarningsByDate = useMemo(() => {
    const q = earningsSearch.trim().toLowerCase();

    const filtered = earnings.filter((it) => {
      const itemDate = it.date || "";
      const moverStatus = isTrueMajorMover(it.symbol);

      if (!showPastDays && itemDate && itemDate < todayDateStr) {
        return false;
      }
      if (majorMoversOnly && !moverStatus) {
        return false;
      }
      if (selectedMarket !== "ALL") {
        const normalized = normalizeMarkets(it.impacted_markets, it.symbol);
        if (!normalized.includes(selectedMarket)) return false;
      }
      if (q) {
        const symbol = String(it?.symbol || "").toLowerCase();
        const name = String(it?.name || "").toLowerCase();
        return symbol.includes(q) || name.includes(q);
      }
      return true;
    });

    // Sort by Date -> Exact Time / Session -> True Major Movers -> Revenue Estimate
    const sorted = [...filtered].sort((a, b) => {
      const dateA = a.date || "9999-12-31";
      const dateB = b.date || "9999-12-31";
      if (dateA !== dateB) return dateA.localeCompare(dateB);

      const minA = parseTimeMinutes(a.exact_time, a.hour_code || a.hour);
      const minB = parseTimeMinutes(b.exact_time, b.hour_code || b.hour);
      if (minA !== minB) return minA - minB;

      const movA = isTrueMajorMover(a.symbol);
      const movB = isTrueMajorMover(b.symbol);
      if (movA !== movB) return movA ? -1 : 1;

      return (b.revenue_estimate || 0) - (a.revenue_estimate || 0);
    });

    const groups = {};
    sorted.forEach((item) => {
      const dKey = item.date || "Upcoming";
      if (!groups[dKey]) groups[dKey] = [];
      groups[dKey].push(item);
    });

    return groups;
  }, [earnings, earningsSearch, majorMoversOnly, selectedMarket, showPastDays, todayDateStr]);

  function formatDateHeader(yyyyMmDd) {
    if (!yyyyMmDd || yyyyMmDd === "Upcoming") return "Upcoming";
    const d = new Date(`${yyyyMmDd}T00:00:00`);
    if (isNaN(d.getTime())) return yyyyMmDd;

    const formatted = d.toLocaleDateString("en-US", {
      weekday: "long",
      month: "short",
      day: "numeric",
      year: "numeric",
    });

    return yyyyMmDd === todayDateStr ? `Today — ${formatted}` : formatted;
  }

  function formatRevenue(rev) {
    if (!rev || rev <= 0) return "—";
    if (rev >= 1_000_000_000) return `$${(rev / 1_000_000_000).toFixed(2)}B`;
    if (rev >= 1_000_000) return `$${(rev / 1_000_000).toFixed(1)}M`;
    return `$${rev.toLocaleString()}`;
  }

  function getSessionBadge(item) {
    const code = (item.hour_code || item.hour || "").toLowerCase();
    const exactTime = item.exact_time || "";

    if (code === "bmo") {
      return {
        exactTime,
        shortCode: "BMO",
        label: "BMO • Before Open",
        window: item.release_window_et || "6:00 AM - 9:30 AM ET",
        classes:
          "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300 border-sky-200 dark:border-sky-800",
      };
    }
    if (code === "amc") {
      return {
        exactTime,
        shortCode: "AMC",
        label: "AMC • After Close",
        window: item.release_window_et || "4:00 PM - 5:00 PM ET",
        classes:
          "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800",
      };
    }
    if (code === "dmh") {
      return {
        exactTime,
        shortCode: "DMH",
        label: "DMH • Market Hours",
        window: item.release_window_et || "9:30 AM - 4:00 PM ET",
        classes:
          "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800",
      };
    }
    return {
      exactTime,
      shortCode: "TBA",
      label: "Time TBA",
      window: "Check Pre/Post Market",
      classes:
        "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600",
    };
  }

  // Cool, clean palette (Blue / Teal / Indigo — no burnt orange/amber)
  const marketBadgeColors = {
    "S&P": "bg-blue-100 text-blue-800 border-blue-300 dark:bg-blue-900/40 dark:text-blue-200 dark:border-blue-700",
    Nasdaq: "bg-teal-100 text-teal-800 border-teal-300 dark:bg-teal-900/40 dark:text-teal-200 dark:border-teal-700",
    Dow: "bg-indigo-100 text-indigo-800 border-indigo-300 dark:bg-indigo-900/40 dark:text-indigo-200 dark:border-indigo-700",
  };

  // --- LIVE TRADINGVIEW HEATMAP LINKS ---
  const heatmapLinks = [
    {
      title: "S&P 500 Live Heatmap",
      subtitle: "Real-time S&P 500 sector & market-cap performance on TradingView",
      badge: "S&P 500",
      url: "https://www.tradingview.com/heatmap/stock/#%7B%22dataSource%22%3A%22SPX500%22%2C%22blockColor%22%3A%22change%7C60%22%2C%22blockSize%22%3A%22market_cap_basic%22%2C%22grouping%22%3A%22sector%22%7D",
      gradient: "from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800",
      badgeBg: "bg-blue-800/60 text-blue-100",
    },
    {
      title: "Nasdaq 100 Live Heatmap",
      subtitle: "Real-time Nasdaq 100 tech & growth leaders on TradingView",
      badge: "Nasdaq 100",
      url: "https://www.tradingview.com/heatmap/stock/#%7B%22dataSource%22%3A%22NASDAQ100%22%2C%22blockColor%22%3A%22change%7C60%22%2C%22blockSize%22%3A%22market_cap_basic%22%2C%22grouping%22%3A%22sector%22%7D",
      gradient: "from-teal-600 to-emerald-700 hover:from-teal-700 hover:to-emerald-800",
      badgeBg: "bg-teal-900/60 text-teal-100",
    },
    {
      title: "Dow Jones 30 Live Heatmap",
      subtitle: "Real-time Dow 30 industrial & blue-chip components on TradingView",
      badge: "Dow 30",
      url: "https://www.tradingview.com/heatmap/stock/#%7B%22dataSource%22%3A%22DJCA%22%2C%22blockColor%22%3A%22change%7C60%22%2C%22blockSize%22%3A%22market_cap_basic%22%2C%22grouping%22%3A%22sector%22%7D",
      gradient: "from-indigo-600 to-purple-700 hover:from-indigo-700 hover:to-purple-800",
      badgeBg: "bg-indigo-900/60 text-indigo-100",
    },
  ];

  // --- TAB CONFIGURATION ---
  const tabs = [
    { key: "events", label: "Events" },
    { key: "earnings", label: "Earnings" },
    { key: "highlights", label: "Highlights" },
    { key: "holidays", label: "CME Holidays" },
    { key: "heatmaps", label: "Index Heatmaps" },
    { key: "forex", label: "Forex Heatmap" },
  ];

  const handleTabClick = (tab) => {
    if (tab.externalUrl) {
      window.open(tab.externalUrl, "_blank", "noopener,noreferrer");
    } else {
      setActiveTab(tab.key);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 transition-colors flex flex-col md:flex-row">
      <main className="flex-1 max-w-5xl mx-auto py-10 px-4">
        <h1 className="text-3xl font-bold mb-5 text-blue-900 dark:text-blue-200">
          News and Economic Data
        </h1>

        <FuturesTicker theme={isDark ? "dark" : "light"} />

        {/* Tabs Navigation */}
        <div className="my-6">
          <nav className="flex flex-wrap md:flex-nowrap justify-between gap-1 p-1 bg-gray-100 dark:bg-gray-800/80 rounded-xl border border-gray-200 dark:border-gray-700">
            {tabs.map((t) => {
              const isActive = activeTab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => handleTabClick(t)}
                  className={`
                    flex-1 flex items-center justify-center gap-1.5 px-2 py-2 text-[11px] lg:text-xs font-bold rounded-lg transition-all duration-200
                    ${
                      isActive
                        ? "bg-blue-600 text-white shadow-md"
                        : "text-gray-600 dark:text-gray-400 hover:bg-white dark:hover:bg-gray-700 hover:text-blue-600 dark:hover:text-blue-400"
                    }
                  `}
                >
                  <span className="text-sm">{t.icon}</span>
                  <span className="hidden sm:inline-block">{t.label}</span>
                  <span className="sm:hidden">{t.label.substring(0, 3)}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* CONTENT RENDER */}
        {activeTab === "events" ? (
          <div className="rounded-xl overflow-hidden shadow-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex flex-col h-[800px]">
            {/* TIMEZONE WARNING BANNER */}
            <div className="bg-amber-50 dark:bg-amber-900/30 border-b border-amber-200 dark:border-amber-800 p-3 flex items-start gap-3 shrink-0">
              <svg
                className="w-5 h-5 text-amber-600 dark:text-amber-500 shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                ></path>
              </svg>
              <div className="text-sm text-amber-800 dark:text-amber-200">
                <strong>Timezone Notice:</strong> This calendar may not display your timezone
                correctly, for example you may have to select Atlantic instead of Eastern for the
                Eastern timezone. Try the next timezone if your local timezone is not displayed as
                expected.
              </div>
            </div>

            {/* MYFXBOOK WIDGET */}
            <div className="flex-1 relative p-4 pb-0 bg-white dark:bg-[#12161f]">
              <iframe
                src="https://widget.myfxbook.com/widget/calendar.html?lang=en&impacts=0,2,3&symbols=AUD,BRL,CAD,CHF,CNY,EUR,GBP,JPY,NZD,USD"
                style={{
                  border: 0,
                  width: "100%",
                  height: "100%",
                  filter: isDark
                    ? "invert(0.9) hue-rotate(200deg) saturate(1.5) contrast(1.1)"
                    : "none",
                }}
                title="Economic Calendar"
              />
            </div>

            {/* MYFXBOOK ATTRIBUTION */}
            <div
              className="p-2 bg-white dark:bg-[#12161f] text-center"
              style={{
                filter: isDark ? "invert(0.92) hue-rotate(180deg)" : "none",
                transition: "filter 0.3s ease",
              }}
            >
              <div
                style={{
                  width: "fit-content",
                  margin: "auto",
                  fontFamily: "roboto,sans-serif!important",
                  fontSize: "13px",
                  color: "#757575",
                }}
              >
                <a
                  href="https://www.myfxbook.com/forex-economic-calendar?utm_source=widget13&utm_medium=link&utm_campaign=copyright"
                  title="Economic Calendar"
                  className="myfxbookLink"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <b style={{ color: "#757575" }}>Economic Calendar</b>
                </a>{" "}
                by Myfxbook.com
              </div>
            </div>
          </div>
        ) : activeTab === "earnings" ? (
          <div className="space-y-6">
            {/* FILTER BAR */}
            <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow border border-gray-200 dark:border-gray-700 space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <FaChartLine className="text-blue-600 dark:text-blue-400" />
                  <span className="font-bold text-gray-900 dark:text-gray-100">
                    Index-Impacting Earnings
                  </span>
                  {earningsPublishTime && (
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      (Updated: {earningsPublishTime})
                    </span>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  {/* Major Movers Toggle (Default ON — Purple) */}
                  <button
                    type="button"
                    onClick={() => setMajorMoversOnly((prev) => !prev)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border transition ${
                      majorMoversOnly
                        ? "bg-purple-600 text-white border-purple-700 shadow-sm"
                        : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-600"
                    }`}
                  >
                    <FaFire className={majorMoversOnly ? "text-purple-200" : "text-purple-500"} />
                    {majorMoversOnly ? "Major Movers Only" : "Showing All Index Earnings"}
                  </button>

                  {/* Show Earlier Days Toggle */}
                  <button
                    type="button"
                    onClick={() => setShowPastDays((prev) => !prev)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${
                      showPastDays
                        ? "bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700"
                        : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-600"
                    }`}
                  >
                    {showPastDays ? "Showing Full Week" : "From Today Forward"}
                  </button>
                </div>
              </div>

              {/* Search & Index Filter Pills */}
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="text"
                  placeholder="Search by ticker (e.g. MSFT, GOOGL, AAPL) or company name..."
                  value={earningsSearch}
                  onChange={(e) => setEarningsSearch(e.target.value)}
                  className="flex-1 px-4 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:border-blue-400 dark:focus:border-blue-500 bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition"
                />

                <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-900 p-1 rounded-lg border border-gray-200 dark:border-gray-700">
                  {["ALL", "S&P", "Nasdaq", "Dow"].map((mkt) => (
                    <button
                      key={mkt}
                      type="button"
                      onClick={() => setSelectedMarket(mkt)}
                      className={`px-3 py-1 rounded-md text-xs font-bold transition ${
                        selectedMarket === mkt
                          ? "bg-blue-600 text-white shadow-sm"
                          : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                      }`}
                    >
                      {mkt}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* EARNINGS LIST */}
            {earningsLoading ? (
              <div className="p-8 text-center text-blue-700 dark:text-blue-200 font-semibold">
                Loading weekly earnings schedule...
              </div>
            ) : Object.keys(groupedEarningsByDate).length === 0 ? (
              <div className="bg-white dark:bg-gray-800 rounded-xl p-8 text-center border border-gray-200 dark:border-gray-700 space-y-3">
                <div className="text-gray-500 dark:text-gray-400 font-medium">
                  No matching earnings found for the selected filters.
                </div>
                {majorMoversOnly && (
                  <button
                    type="button"
                    onClick={() => setMajorMoversOnly(false)}
                    className="text-xs font-bold text-blue-600 dark:text-blue-400 underline"
                  >
                    Switch to Show All Index Earnings
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-6">
                {Object.entries(groupedEarningsByDate).map(([dateStr, items]) => (
                  <div
                    key={dateStr}
                    className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 overflow-hidden"
                  >
                    {/* Date Header */}
                    <div
                      className={`px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between ${
                        dateStr === todayDateStr
                          ? "bg-blue-600 text-white"
                          : "bg-gray-100 dark:bg-gray-700/60 text-gray-800 dark:text-gray-100"
                      }`}
                    >
                      <div className="font-bold text-sm md:text-base">
                        {formatDateHeader(dateStr)}
                      </div>
                      <span
                        className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${
                          dateStr === todayDateStr
                            ? "bg-blue-800 text-blue-100"
                            : "bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200"
                        }`}
                      >
                        {items.length} {items.length === 1 ? "Report" : "Reports"}
                      </span>
                    </div>

                    {/* Company Rows (TIME FIRST ON THE LEFT) */}
                    <div className="divide-y divide-gray-100 dark:divide-gray-700">
                      {items.map((it, idx) => {
                        const session = getSessionBadge(it);
                        const companyName =
                          it.name && it.name !== it.symbol ? it.name : it.company || it.symbol;
                        const markets = normalizeMarkets(it.impacted_markets, it.symbol);
                        const moverStatus = isTrueMajorMover(it.symbol);

                        return (
                          <div
                            key={`${it.symbol}-${idx}`}
                            className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/40 transition flex flex-col sm:flex-row sm:items-center gap-4"
                          >
                            {/* 1. FIRST COLUMN: Release Time & Session */}
                            <div className="sm:w-44 shrink-0 flex flex-row sm:flex-col items-center sm:items-start justify-between sm:justify-center gap-2 sm:border-r border-gray-100 dark:border-gray-700 sm:pr-4">
                              {session.exactTime ? (
                                <>
                                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-sm font-extrabold bg-emerald-600 text-white shadow-sm">
                                    <FaClock size={12} />
                                    {session.exactTime}
                                  </span>
                                  <span className="text-[11px] font-semibold text-gray-500 dark:text-gray-400">
                                    {session.label}
                                  </span>
                                </>
                              ) : (
                                <>
                                  <span
                                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-bold border ${session.classes}`}
                                  >
                                    <FaClock size={11} />
                                    {session.label}
                                  </span>
                                  <span className="text-[11px] text-gray-500 dark:text-gray-400">
                                    {session.window}
                                  </span>
                                </>
                              )}
                            </div>

                            {/* 2. SECOND COLUMN: Ticker, Company Name, Multi-Index Badges & Estimates */}
                            <div className="flex-1 space-y-1.5">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="px-2.5 py-0.5 rounded-md text-sm font-extrabold bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900">
                                  {it.symbol}
                                </span>

                                <span className="font-bold text-base text-gray-900 dark:text-gray-100">
                                  {companyName}
                                </span>

                                {moverStatus && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-200 border border-purple-300 dark:border-purple-700">
                                    <FaFire className="text-purple-600 dark:text-purple-300" size={10} />
                                    Major Mover
                                  </span>
                                )}

                                {markets.map((mkt) => (
                                  <span
                                    key={mkt}
                                    className={`px-2 py-0.5 rounded text-[11px] font-extrabold border ${
                                      marketBadgeColors[mkt] ||
                                      "bg-gray-100 text-gray-800 border-gray-300"
                                    }`}
                                  >
                                    {mkt}
                                  </span>
                                ))}
                              </div>

                              {/* Estimates */}
                              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400">
                                <span>
                                  EPS Est:{" "}
                                  <strong className="text-gray-700 dark:text-gray-200">
                                    {it.eps_estimate != null
                                      ? `$${Number(it.eps_estimate).toFixed(2)}`
                                      : "—"}
                                  </strong>
                                </span>
                                <span>
                                  Rev Est:{" "}
                                  <strong className="text-gray-700 dark:text-gray-200">
                                    {formatRevenue(it.revenue_estimate)}
                                  </strong>
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : activeTab === "holidays" ? (
          <div className="space-y-8 animate-fadeIn">
            <div className="p-4 text-sm text-blue-800 dark:text-blue-200 flex items-start gap-3">
              <FaCalendarAlt className="mt-0.5 text-blue-500 shrink-0" size={16} />
              <div>
                <strong>Market Holidays:</strong> Dates listed indicate general market closures or
                significant halts. Always check the official{" "}
                <a
                  href="https://www.cmegroup.com/tools-information/holiday-calendar.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-blue-600 font-semibold"
                >
                  CME Holiday Calendar
                </a>{" "}
                for specific product hours and early close times.
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden transform transition hover:-translate-y-1 duration-300">
                <div className="bg-gradient-to-r from-purple-600 to-purple-500 p-4">
                  <div className="flex items-center justify-between text-white">
                    <h3 className="text-xl font-bold">2026</h3>
                    <span className="text-xs bg-purple-700/50 px-2 py-1 rounded-md uppercase tracking-wider font-semibold">
                      Current Year
                    </span>
                  </div>
                </div>
                <div className="divide-y divide-gray-100 dark:divide-gray-700">
                  {HOLIDAYS_2026.map((h, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      <div className="flex flex-col items-center justify-center w-12 h-12 bg-purple-50 dark:bg-purple-900/30 rounded-lg border border-purple-100 dark:border-purple-800 shrink-0">
                        <span className="text-[10px] uppercase font-bold text-purple-600 dark:text-purple-400">
                          {new Date(h.date + "T00:00:00").toLocaleDateString("en-US", {
                            month: "short",
                          })}
                        </span>
                        <span className="text-lg font-bold text-gray-800 dark:text-gray-100 leading-none">
                          {new Date(h.date + "T00:00:00").getDate()}
                        </span>
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900 dark:text-gray-100">
                          {h.name}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {new Date(h.date + "T00:00:00").toLocaleDateString("en-US", {
                            weekday: "long",
                          })}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : activeTab === "highlights" ? (
          <div className="rounded-xl overflow-hidden shadow-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            <div className="w-full">
              <FinancialJuiceWidget
                mode={isDark ? "Dark" : "Light"}
                width="100%"
                height="800px"
                backColorDark="1e222d"
                fontColorDark="b2b5be"
                backColorLight="ffffff"
                fontColorLight="111827"
              />
            </div>
          </div>
        ) : activeTab === "forex" ? (
          <div className="rounded-xl overflow-hidden shadow-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            <div className="w-full h-[720px]">
              <ForexHeatmapWidget theme={isDark ? "dark" : "light"} />
            </div>
          </div>
        ) : activeTab === "heatmaps" ? (
          /* DIRECT EXTERNAL BUTTONS TO REAL-TIME TRADINGVIEW HEATMAPS */
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 space-y-6">
            <div className="space-y-1">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                Real-Time US Index Heatmaps
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Open the live, non-delayed TradingView heatmaps directly in a new tab to monitor real-time sector and mega-cap money flow.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4">
              {heatmapLinks.map((hm) => (
                <a
                  key={hm.badge}
                  href={hm.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`bg-gradient-to-r ${hm.gradient} text-white rounded-xl p-5 shadow-md hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 flex items-center justify-between gap-4`}
                  style={{ textDecoration: "none" }}
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-2.5">
                      <span className="text-lg font-extrabold">{hm.title}</span>
                      <span className={`text-xs font-bold px-2.5 py-0.5 rounded-full ${hm.badgeBg}`}>
                        {hm.badge}
                      </span>
                    </div>
                    <p className="text-xs sm:text-sm text-white/90">{hm.subtitle}</p>
                  </div>

                  <div className="shrink-0 flex items-center gap-2 bg-white/15 hover:bg-white/25 px-4 py-2.5 rounded-lg text-xs sm:text-sm font-bold backdrop-blur-sm transition">
                    <span>Open Live Heatmap</span>
                    <FaExternalLinkAlt size={12} />
                  </div>
                </a>
              ))}
            </div>
          </div>
        ) : null}

        <div className="mt-10 rounded-xl overflow-hidden shadow-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <TradingViewWidget theme={isDark ? "dark" : "light"} />
        </div>
      </main>
    </div>
  );
}

function ForexHeatmapWidget({ theme }) {
  const container = useRef();

  useEffect(() => {
    if (!container.current) return;
    container.current.innerHTML = "";

    const script = document.createElement("script");
    script.src = "https://s3.tradingview.com/external-embedding/embed-widget-forex-heat-map.js";
    script.type = "text/javascript";
    script.async = true;

    script.innerHTML = JSON.stringify({
      width: "100%",
      height: "100%",
      currencies: ["EUR", "USD", "JPY", "GBP", "CHF", "AUD", "CAD", "NZD"],
      isTransparent: false,
      colorTheme: theme,
      locale: "en",
      backgroundColor: theme === "dark" ? "#1F2937" : "#FFFFFF",
    });

    container.current.appendChild(script);
  }, [theme]);

  return (
    <div className="tradingview-widget-container h-full w-full" ref={container}>
      <div className="tradingview-widget-container__widget h-full w-full"></div>
    </div>
  );
}

function TradingViewWidget({ theme }) {
  useEffect(() => {
    const old = document.getElementById("tradingview-widget-script");
    if (old) old.remove();

    const script = document.createElement("script");
    script.id = "tradingview-widget-script";
    script.src = "https://s3.tradingview.com/external-embedding/embed-widget-ticker-tape.js";
    script.async = true;
    script.innerHTML = JSON.stringify({
      symbols: [
        { proName: "OANDA:SPX500USD", title: "S&P 500" },
        { proName: "OANDA:NAS100USD", title: "Nasdaq 100" },
        { proName: "FOREXCOM:DJI", title: "Dow Jones" },
        { proName: "TVC:GOLD", title: "Gold" },
        { proName: "TVC:SILVER", title: "Silver" },
        { proName: "TVC:USOIL", title: "Crude Oil" },
        { proName: "FX:EURUSD", title: "EUR/USD" },
      ],
      colorTheme: theme,
      isTransparent: false,
      displayMode: "adaptive",
      locale: "en",
    });

    const container = document.getElementById("tradingview-widget-container");
    if (container) container.appendChild(script);
  }, [theme]);

  return <div id="tradingview-widget-container" className="w-full h-18" />;
}

function FinancialJuiceWidget({
  mode = "Dark",
  height = 800,
  backColorDark = "1e222d",
  fontColorDark = "b2b5be",
  backColorLight = "ffffff",
  fontColorLight = "111827",
}) {
  const hostRef = useRef(null);
  const [pxWidth, setPxWidth] = useState(null);
  const containerId = useMemo(() => `fj-container-${Math.random().toString(36).slice(2)}`, []);

  useEffect(() => {
    const el = hostRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const cr = entries[0]?.contentRect;
      if (!cr) return;
      const w = Math.floor(cr.width);
      setPxWidth((prev) => (prev !== w ? w : prev));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const ensureScript = useCallback(
    () =>
      new Promise((resolve) => {
        const existing = document.getElementById("FJ-Widgets");
        if (existing) {
          if (window.FJWidgets) resolve();
          else existing.addEventListener("load", resolve, { once: true });
          return;
        }
        const s = document.createElement("script");
        s.type = "text/javascript";
        s.async = true;
        s.id = "FJ-Widgets";
        s.src = `https://feed.financialjuice.com/widgets/widgets.js?r=${Date.now()}`;
        s.onload = () => resolve();
        document.head.appendChild(s);
      }),
    []
  );

  useEffect(() => {
    let cancelled = false;
    if (pxWidth == null) return;

    ensureScript().then(() => {
      if (cancelled || !window.FJWidgets) return;
      const mount = document.getElementById(containerId);
      if (mount) mount.innerHTML = "";

      const options = {
        container: containerId,
        mode,
        width: `${pxWidth}px`,
        height: `${height}px`,
        backColor: mode === "Dark" ? backColorDark : backColorLight,
        fontColor: mode === "Dark" ? fontColorDark : fontColorLight,
        widgetType: "NEWS",
      };

      requestAnimationFrame(() => {
        if (!cancelled) {
          const originalCreateElement = document.createElement;

          document.createElement = function (tagName) {
            const el = originalCreateElement.call(document, tagName);
            if (tagName.toLowerCase() === "iframe") {
              el.setAttribute("allow", "clipboard-write; clipboard-read");
            }
            return el;
          };

          try {
            new window.FJWidgets.createWidget(options);
          } finally {
            document.createElement = originalCreateElement;
          }
        }
      });
    });

    return () => {
      cancelled = true;
      const mount = document.getElementById(containerId);
      if (mount) mount.innerHTML = "";
    };
  }, [
    ensureScript,
    containerId,
    pxWidth,
    mode,
    height,
    backColorDark,
    backColorLight,
    fontColorDark,
    fontColorLight,
  ]);

  return (
    <div ref={hostRef} className="w-full">
      <div id={containerId} style={{ width: "100%", minHeight: `${height}px` }} />
    </div>
  );
}